import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBooking } from '../context/BookingContext';
import DatePicker from './DatePicker';
import { apiService } from '../services/apiService';
import CustomDatePicker from './CustomDatePicker';

const SearchBar = () => {
  const { state, dispatch } = useBooking();
  
  // ==========================================================
  // --- MOBILE UI STATE ---
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false); // Level 1: Main Mobile Modal
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);   // Level 2: Destination Search View
  const [isGuestSheetOpen, setIsGuestSheetOpen] = useState(false);     // Level 2: Guest Bottom Sheet
  // ==========================================================

  // Desktop Toggles
  const [hotelDropdownOpen, setHotelDropdownOpen] = useState(false);
  const [guestsDropdownOpen, setGuestsDropdownOpen] = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState({});

  // Data State
  const [hotelSearch, setHotelSearch] = useState('');
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // --- PROMO CODE STATE ---
const [isPromoOpen, setIsPromoOpen] = useState(false);
const [promoInput, setPromoInput] = useState(state.searchState.promo_code || '');
const [promoLoading, setPromoLoading] = useState(false);
const [promoError, setPromoError] = useState('');
const [promoApplied, setPromoApplied] = useState(!!state.searchState.promo_code);
const promoRef = useRef(null);

 // Flag to track if widget promo has been applied
  const [widgetPromoApplied, setWidgetPromoApplied] = useState(false);
  const [justApplied, setJustApplied] = useState(false);


  // Add this to your SearchBar component, near your other useEffect hooks
useEffect(() => {
  const fetchInitialHotelLogo = async () => {
    // Only fetch if we have a hotel ID and no logo is stored yet
    const existingLogo = sessionStorage.getItem('hotel_logo');
    if (existingLogo) {
      console.log('Logo already exists in sessionStorage');
      return;
    }
    
    const hotelId = state.searchState.hotelId;
    if (!hotelId) return;
    
    const currentCheckin = state.searchState.checkin;
    const currentCheckout = state.searchState.checkout;
    
    if (!currentCheckin || !currentCheckout) {
      console.log('No dates selected, skipping initial logo fetch');
      return;
    }
    
    try {
      const formatDate = (d) => {
        const date = new Date(d);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(currentCheckin),
        endDate: formatDate(currentCheckout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
      };
      
      const hotelData = await apiService.getHotelData(hotelId, searchParams);
      
      if (hotelData?.hotel_info?.hotel_logo) {
        sessionStorage.setItem('hotel_logo', hotelData.hotel_info.hotel_logo);
        sessionStorage.setItem('hotel_name', hotelData.hotel_info.name);
        sessionStorage.setItem('hotel_cancel_policy', hotelData.hotel_info.hotel_cancel_policy);
        console.log('🏨 Initial logo fetched and stored');
        
        window.dispatchEvent(new CustomEvent('hotelDataUpdate', {
          detail: {
            hotelLogo: hotelData.hotel_info.hotel_logo,
            hotelName: hotelData.hotel_info.name
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching initial hotel logo:', error);
    }
  };
  
  // Small delay to ensure everything is loaded
  const timer = setTimeout(fetchInitialHotelLogo, 500);
  return () => clearTimeout(timer);
}, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout]);


  // --- API LOGIC ---
 const fetchHotels = useCallback(async (searchTerm = '') => {
    try {
      setHotelsLoading(true);
      const hotelData = await apiService.getHotelList(searchTerm);
      setHotels(hotelData);
      
      if (hotelData.length > 0 && initialLoad && searchTerm === '') {
        const savedHotelId = state.searchState.hotelId;
        
        if (savedHotelId && state.searchState.hotel && state.searchState.hotel !== "ABC Hotel") {
          setInitialLoad(false);
          return;
        }
        
        if (savedHotelId) {
          const savedHotel = hotelData.find(h => h.id === savedHotelId);
          if (savedHotel) {
            dispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: { 
                hotel: savedHotel.name, 
                hotelId: savedHotel.id 
              }
            });
          } else {
            dispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: { 
                hotel: hotelData[0].name, 
                hotelId: hotelData[0].id 
              }
            });
          }
        } else {
          dispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: { 
              hotel: hotelData[0].name, 
              hotelId: hotelData[0].id 
            }
          });
        }
        setInitialLoad(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setHotelsLoading(false);
    }
  }, [dispatch, initialLoad, state.searchState.hotelId, state.searchState.hotel]); // Add hotel to dependencies

  useEffect(() => { fetchHotels(); }, [fetchHotels]);

  // Debounce Search
    useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(hotelSearch || hotelDropdownOpen || isDestinationOpen) fetchHotels(hotelSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [hotelSearch, hotelDropdownOpen, isDestinationOpen, fetchHotels]);

  // --- SCROLL LOCK FOR MOBILE MODALS ---
useEffect(() => {
    const lock = isMobileSearchOpen || isDestinationOpen || isGuestSheetOpen;
    document.body.style.overflow = lock ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isMobileSearchOpen, isDestinationOpen, isGuestSheetOpen]);

  // Close promo popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (promoRef.current && !promoRef.current.contains(e.target)) {
        setIsPromoOpen(false);
      }
    };
    if (isPromoOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPromoOpen]);

// Sync promoInput if promo_code gets cleared externally (e.g. hotel change)
  useEffect(() => {
    if (!state.searchState.promo_code) {
      setPromoInput('');
      setPromoApplied(false);
      setPromoError('');
      setWidgetPromoApplied(false);
    }
  }, [state.searchState.promo_code]);

// Clear promo when dates change - but only for manual promos and not right after application
// Clear promo when dates change - but only for manual promos and not right after application
// useEffect(() => {
//   // Skip if we have a promo code in state (it was just applied)
//   if (justApplied || state.searchState.promo_code) {
//     if (justApplied) {
//       console.log('⏸️ Skipping clear - just applied promo');
//     } else if (state.searchState.promo_code) {
//       console.log('⏸️ Skipping clear - promo exists in state');
//     }
//     return;
//   }
  
//   const isFromWidget = state.searchState.promo_from_widget;
//   const shouldClear = promoApplied && !isFromWidget && !widgetPromoApplied;
  
//   if (shouldClear) {
//     console.log('🗑️ Clearing promo due to date change');
//     dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
//     setPromoInput('');
//     setPromoApplied(false);
//     setPromoError('');
//   }
// }, [state.searchState.checkin, state.searchState.checkout, promoApplied, widgetPromoApplied, state.searchState.promo_from_widget, dispatch, justApplied, state.searchState.promo_code]);


// Add this ref to track previous dates
const prevDatesRef = useRef({ checkin: null, checkout: null });

// Function to handle date changes
const handleDatesChange = useCallback((newCheckin, newCheckout) => {
  // Check if dates actually changed
  const datesChanged = 
    prevDatesRef.current.checkin !== newCheckin ||
    prevDatesRef.current.checkout !== newCheckout;
  
  if (datesChanged) {
    console.log('📅 Dates changed, clearing promo code');
    
    // Clear all promo-related state
    dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
    setPromoInput('');
    setPromoApplied(false);
    setPromoError('');
    setWidgetPromoApplied(false);
    
    // Update the previous dates reference
    prevDatesRef.current = {
      checkin: newCheckin,
      checkout: newCheckout
    };
  }
}, [dispatch]);


useEffect(() => {
  // Skip if we just applied a promo
  if (justApplied) {
    return;
  }
  
  // Get current dates
  const currentCheckin = state.searchState.checkin;
  const currentCheckout = state.searchState.checkout;
  
  // Skip on initial load
  if (prevDatesRef.current.checkin === null && prevDatesRef.current.checkout === null) {
    prevDatesRef.current = {
      checkin: currentCheckin,
      checkout: currentCheckout
    };
    return;
  }
  
  // Handle date change
  handleDatesChange(currentCheckin, currentCheckout);
  
}, [state.searchState.checkin, state.searchState.checkout, justApplied, handleDatesChange]);


// Watch for promo applied state and update UI accordingly
useEffect(() => {
  console.log('🎨 UI Update Effect - promoApplied:', promoApplied, 'promo_code:', state.searchState.promo_code);
  
  // Don't update if we just applied
  if (justApplied) return;
  
  if (state.searchState.promo_code && !promoInput) {
    setPromoInput(state.searchState.promo_code);
  }
  
  if (state.searchState.promo_code && !promoApplied && state.searchState.promo_from_widget) {
    setPromoApplied(true);
    setWidgetPromoApplied(true);
  }
}, [state.searchState.promo_code, state.searchState.promo_from_widget, promoApplied, promoInput, justApplied]);


 useEffect(() => {
  const autoApplyWidgetPromo = async () => {
    const widgetPromoCode = state.searchState.promo_code;
    const isFromWidget = state.searchState.promo_from_widget;
    
    console.log('🔍 Auto-apply widget promo check:', {
      widgetPromoCode,
      isFromWidget,
      widgetPromoApplied,
      promoApplied,
      hasHotel: !!state.searchState.hotelId,
      hasDates: !!(state.searchState.checkin && state.searchState.checkout)
    });
    
    if (!widgetPromoCode || !isFromWidget || widgetPromoApplied || promoApplied) {
      return;
    }
    
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      console.log('⏳ Waiting for hotel and dates to load...');
      return;
    }
    
    console.log('🎯 Auto-applying widget promo code:', widgetPromoCode);
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const formatDate = (d) => {
        const date = new Date(d);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(state.searchState.checkin),
        endDate: formatDate(state.searchState.checkout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
        promo_code: widgetPromoCode,
      };
      
      const hotelData = await apiService.getHotelData(
        state.searchState.hotelId,
        searchParams
      );
      
      const promoCodeInfo = hotelData?.promo_code_info;
      
      console.log('📦 API Response - promo_code_info:', promoCodeInfo);
      
      if (!promoCodeInfo || promoCodeInfo.is_valid === false) {
        const errorMessage = promoCodeInfo?.message || 'Invalid promo code';
        console.warn('❌ Widget promo code invalid:', widgetPromoCode, errorMessage);
        setPromoError(errorMessage);
        
        dispatch({
          type: 'UPDATE_SEARCH_STATE',
          payload: { promo_code: '', promo_from_widget: false }
        });
        setWidgetPromoApplied(true);
        setPromoApplied(false);
        setPromoInput('');
        setPromoLoading(false);
        return;
      }
      
     // Inside your autoApplyWidgetPromo function, when the promo is successfully applied:
if (promoCodeInfo.applied === true) {
  console.log('✅ Widget promo valid and applied!');
  
  // Set justApplied flag to prevent immediate clearing
  setJustApplied(true);
  
 // Calculate total discount from rate plans
        let totalDiscount = 0;
        hotelData?.categories?.forEach(category => {
          category?.rate_plans?.forEach(plan => {
            if (plan?.offer?.is_promo_code === true && plan?.offer?.promo_code === widgetPromoCode) {
              const saving = (plan.originalPrice || 0) - (plan.price || 0);
              if (saving > 0) totalDiscount += saving;
              console.log(`💰 Discount for ${plan.name}: ${saving}`);
            }
          });
        });
        
        console.log('✅ Total discount amount:', totalDiscount);
        
        // CRITICAL: Update all state to show promo as applied
        setPromoApplied(true);
        setPromoInput(widgetPromoCode);
        setPromoError('');
        setWidgetPromoApplied(true);
        
        // Close the promo popup if open
        setIsPromoOpen(false);
        
        // Apply the promo discount to context
        dispatch({
          type: 'APPLY_WIDGET_PROMO',
          payload: {
            discountAmount: totalDiscount,
            promoCode: widgetPromoCode,
          },
        });
        
        // Update search state with API response
        dispatch({
          type: 'PERFORM_SEARCH',
          payload: {
            hotelId: state.searchState.hotelId,
            checkin: state.searchState.checkin,
            checkout: state.searchState.checkout,
            rooms: state.rooms.length,
            guests: state.searchState.guestCount,
            promo_code: widgetPromoCode,
            apiResponse: hotelData
          }
        });
        
 
  // Reset justApplied flag after a short delay
  setTimeout(() => {
     console.log('🔄 Triggering search results update');
    setJustApplied(false);
  }, 500);
} else {
        setPromoError(promoCodeInfo?.message || 'Promo code could not be applied');
        setPromoApplied(false);
      }
      
    } catch (err) {
      console.error('❌ Widget promo validation error:', err);
      setPromoError('Could not validate promo code. Please try again.');
    } finally {
      setPromoLoading(false);
    }
  };
  
  if (state.searchState.promo_code && state.searchState.promo_from_widget && !widgetPromoApplied) {
    const timer = setTimeout(autoApplyWidgetPromo, 1000);
    return () => clearTimeout(timer);
  }
}, [state.searchState.promo_code, state.searchState.promo_from_widget, state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, widgetPromoApplied, promoApplied, dispatch, state.rooms, state.searchState.guestCount]);

const handleApplyPromo = async () => {
  if (!promoInput.trim()) return;

  if (!state.searchState.hotelId) {
    setPromoError('Please select a hotel first.');
    return;
  }
  if (!state.searchState.checkin || !state.searchState.checkout) {
    setPromoError('Please select check-in and check-out dates first.');
    return;
  }

  setPromoLoading(true);
  setPromoError('');

  try {
    const formatDate = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const searchParams = {
      startDate: formatDate(state.searchState.checkin),
      endDate: formatDate(state.searchState.checkout),
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      roomsData: state.rooms,
      promo_code: promoInput.trim(),
    };

    const hotelData = await apiService.getHotelData(
      state.searchState.hotelId,
      searchParams
    );

      if (hotelData?.hotel_info?.hotel_logo) {
          sessionStorage.setItem('hotel_logo', hotelData.hotel_info.hotel_logo);
          sessionStorage.setItem('hotel_name', hotelData.hotel_info.name);
          sessionStorage.setItem('hotel_cancel_policy', hotelData.hotel_info.hotel_cancel_policy);
          console.log( 'hotel logo Stored',hotelData.hotel_info.hotel_logo)
          // Dispatch custom event for real-time update
          window.dispatchEvent(new CustomEvent('hotelDataUpdate', {
            detail: {
              hotelLogo: hotelData.hotel_info.hotel_logo,
              hotelName: hotelData.hotel_info.name
            }
          }));
        }

    const promoCodeInfo = hotelData?.promo_code_info;
    
    console.log('📦 Manual promo validation response:', promoCodeInfo);
    
    if (!promoCodeInfo || promoCodeInfo.is_valid === false) {
      setPromoError(promoCodeInfo?.message || 'Invalid promo code');
      setPromoApplied(false);
      setPromoLoading(false);
      return;
    }

    if (promoCodeInfo.applied === true) {
      // Calculate total discount from rate plans
      let totalDiscount = 0;
      hotelData?.categories?.forEach(category => {
        category?.rate_plans?.forEach(plan => {
          if (plan?.offer?.is_promo_code === true && plan?.offer?.promo_code === promoInput.trim()) {
            const saving = (plan.originalPrice || 0) - (plan.price || 0);
            if (saving > 0) totalDiscount += saving;
          }
        });
      });

      // Set justApplied flag to prevent immediate clearing
      setJustApplied(true);
      
      // Update state first
      setPromoApplied(true);
      setPromoError('');
      setPromoInput(promoInput.trim());
      
      // Close the promo popup
      setIsPromoOpen(false);
      
      // Update context
      dispatch({
        type: 'UPDATE_SEARCH_STATE',
        payload: { 
          promo_code: promoInput.trim(),
          promo_from_widget: false
        },
      });

      dispatch({
        type: 'PERFORM_SEARCH',
        payload: {
          hotelId: state.searchState.hotelId,
          checkin: state.searchState.checkin,
          checkout: state.searchState.checkout,
          rooms: state.rooms.length,
          guests: state.searchState.guestCount,
          promo_code: promoInput.trim(),
          apiResponse: hotelData,
        },
      });

      dispatch({
        type: 'APPLY_WIDGET_PROMO',
        payload: {
          discountAmount: totalDiscount,
          promoCode: promoInput.trim(),
        },
      });
      
      console.log('✅ Manual promo applied successfully');
      
      // Reset justApplied flag after a short delay
      setTimeout(() => {
        setJustApplied(false);
      }, 500);
      
    } else {
      setPromoError(promoCodeInfo?.message || 'Promo code could not be applied');
      setPromoApplied(false);
    }

  } catch (err) {
    console.error('Promo validation error:', err);
    setPromoError('Could not validate code. Please try again.');
    setPromoApplied(false);
  } finally {
    setPromoLoading(false);
  }
};
// Debug useEffect to track promo state changes
useEffect(() => {
  console.log('🔍 Promo State Debug:', {
    promoApplied,
    promoInput,
    statePromoCode: state.searchState.promo_code,
    promo_from_widget: state.searchState.promo_from_widget,
    widgetPromoApplied,
    promoError
  });
}, [promoApplied, promoInput, state.searchState.promo_code, state.searchState.promo_from_widget, widgetPromoApplied, promoError]);

const handleClearPromo = (e) => {
  dispatch({ type: 'CLEAR_CART' });
  if (e) e.stopPropagation();
  setJustApplied(false); // Reset the flag
  dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
  setPromoInput('');
  setPromoApplied(false);
  setPromoError('');
  setWidgetPromoApplied(false);
  setIsPromoOpen(false);
};
  // --- HANDLERS ---
  const filteredHotels = hotels.filter(hotel =>
    hotel.name.toLowerCase().includes(hotelSearch.toLowerCase())
  );

//  const handleHotelSelect = (hotel) => {
//     dispatch({ type: 'CLEAR_CART' });
//     dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
//     setPromoInput('');
//     setPromoApplied(false);
//     setPromoError('');
//     setWidgetPromoApplied(false);

//     dispatch({ 
//       type: 'UPDATE_SEARCH_STATE', 
//       payload: { 
//         hotel: hotel.name, 
//         hotelId: hotel.id,
//         searchPerformed: false
//       } 
//     });

//     try {
//       const currentSession = JSON.parse(sessionStorage.getItem('bookingSession') || '{}');
//       currentSession.searchState = {
//         ...currentSession.searchState,
//         hotel: hotel.name,
//         hotelId: hotel.id,
//         searchPerformed: false,
//         promo_code: '',
//         promo_from_widget: false
//       };
//       currentSession.cart = [];
//       currentSession.subtotal = 0;
//       currentSession.total = 0;
//       currentSession.finalTotal = 0;
//       currentSession.taxes = 0;
//       currentSession.hasDiscount = false;
//       currentSession.discountAmount = 0;
//       currentSession.promoCode = '';
//       currentSession.offerDiscount = 0;
      
//       sessionStorage.setItem('bookingSession', JSON.stringify(currentSession));
//     } catch (error) {
//       console.error('Error updating session storage:', error);
//     }

//     setHotelDropdownOpen(false);
//     setIsDestinationOpen(false);
//     setHotelSearch('');
//   };

  const handleHotelSelect = async (hotel) => {
  dispatch({ type: 'CLEAR_CART' });
  dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
  setPromoInput('');
  setPromoApplied(false);
  setPromoError('');
  setWidgetPromoApplied(false);

  dispatch({ 
    type: 'UPDATE_SEARCH_STATE', 
    payload: { 
      hotel: hotel.name, 
      hotelId: hotel.id,
      searchPerformed: false
    } 
  });

  try {
    // Fetch hotel data immediately to get logo
    const currentCheckin = state.searchState.checkin;
    const currentCheckout = state.searchState.checkout;
    
    if (currentCheckin && currentCheckout) {
      const formatDate = (d) => {
        const date = new Date(d);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(currentCheckin),
        endDate: formatDate(currentCheckout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
      };
      
      const hotelData = await apiService.getHotelData(hotel.id, searchParams);
      
      if (hotelData?.hotel_info?.hotel_logo) {
        sessionStorage.setItem('hotel_logo', hotelData.hotel_info.hotel_logo);
        sessionStorage.setItem('hotel_name', hotelData.hotel_info.name);
        sessionStorage.setItem('hotel_cancel_policy', hotelData.hotel_info.hotel_cancel_policy);
        console.log('🏨 Hotel logo stored for:', hotel.name, hotelData.hotel_info.hotel_logo);
        
        // Dispatch custom event for real-time update
        window.dispatchEvent(new CustomEvent('hotelDataUpdate', {
          detail: {
            hotelLogo: hotelData.hotel_info.hotel_logo,
            hotelName: hotelData.hotel_info.name
          }
        }));
      }
    }
    
    const currentSession = JSON.parse(sessionStorage.getItem('bookingSession') || '{}');
    currentSession.searchState = {
      ...currentSession.searchState,
      hotel: hotel.name,
      hotelId: hotel.id,
      searchPerformed: false,
      promo_code: '',
      promo_from_widget: false
    };
    currentSession.cart = [];
    currentSession.subtotal = 0;
    currentSession.total = 0;
    currentSession.finalTotal = 0;
    currentSession.taxes = 0;
    currentSession.hasDiscount = false;
    currentSession.discountAmount = 0;
    currentSession.promoCode = '';
    currentSession.offerDiscount = 0;
    
    sessionStorage.setItem('bookingSession', JSON.stringify(currentSession));
  } catch (error) {
    console.error('Error fetching hotel data:', error);
  }

  setHotelDropdownOpen(false);
  setIsDestinationOpen(false);
  setHotelSearch('');
  };

   const handleGuestsUpdate = (rooms) => {
    dispatch({ type: 'CLEAR_CART' });
    dispatch({ type: 'UPDATE_ROOMS', payload: rooms });
    const totalAdults = rooms.reduce((acc, r) => acc + r.adults, 0);
    const totalChildren = rooms.reduce((acc, r) => acc + r.children, 0);
    const totalGuests = totalAdults + totalChildren; 
    const guestsText = `${rooms.length} Room${rooms.length !== 1 ? 's' : ''}, ${totalGuests} Guest${totalGuests !== 1 ? 's' : ''}`;
    
    const rooms_guests = rooms.map((room, index) => ({
      room_no: index + 1, 
      no_of_adults: room.adults, 
      no_of_children: room.children,
      child_ages: room.childAges || [] 
    }));

    dispatch({
      type: 'UPDATE_SEARCH_STATE',
      payload: { guestsText, guestCount: totalGuests, room_guest: rooms_guests }
    });
    setGuestsDropdownOpen(false);
  };

   const handleSearch = async () => {
    console.log('🔍 handleSearch called');
    
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      alert('Please select hotel, check-in, and check-out dates');
      return;
    }
    
    const formatDate = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const searchParams = {
      startDate: formatDate(state.searchState.checkin),
      endDate: formatDate(state.searchState.checkout),
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      roomsData: state.rooms,
      promo_code: state.searchState.promo_code || null,
    };

    try {
      setSearchLoading(true);
      const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
      if (hotelData?.hotel_info?.hotel_logo) {
          sessionStorage.setItem('hotel_logo', hotelData.hotel_info.hotel_logo);
          sessionStorage.setItem('hotel_name', hotelData.hotel_info.name);
          sessionStorage.setItem('hotel_cancel_policy', hotelData.hotel_info.hotel_cancel_policy);
          console.log( 'hotel logo Stored',hotelData.hotel_info.hotel_logo)
          // Dispatch custom event for real-time update
          window.dispatchEvent(new CustomEvent('hotelDataUpdate', {
            detail: {
              hotelLogo: hotelData.hotel_info.hotel_logo,
              hotelName: hotelData.hotel_info.name
            }
          }));
        }
      
      const promoCodeInfo = hotelData?.promo_code_info;
      
      if (state.searchState.promo_code && promoCodeInfo) {
        if (promoCodeInfo.is_valid === true && promoCodeInfo.applied === true) {
          // Calculate discount
          let totalDiscount = 0;
          hotelData?.categories?.forEach(category => {
            category?.rate_plans?.forEach(plan => {
              if (plan?.offer?.is_promo_code === true && plan?.offer?.promo_code === state.searchState.promo_code) {
                const saving = (plan.originalPrice || 0) - (plan.price || 0);
                if (saving > 0) totalDiscount += saving;
              }
            });
          });
          
          dispatch({
            type: 'APPLY_WIDGET_PROMO',
            payload: {
              discountAmount: totalDiscount,
              promoCode: state.searchState.promo_code,
            },
          });
          setPromoApplied(true);
          setPromoError('');
        } else {
          setPromoError(promoCodeInfo?.message || 'Invalid promo code');
          setPromoApplied(false);
        }
      }
      
      dispatch({
        type: 'PERFORM_SEARCH',
        payload: {
          hotelId: state.searchState.hotelId,
          checkin: state.searchState.checkin,
          checkout: state.searchState.checkout,
          rooms: state.rooms.length,
          guests: state.searchState.guestCount,
          promo_code: state.searchState.promo_code || null,
          apiResponse: hotelData
        }
      });
      
      setIsMobileSearchOpen(false);

    } catch (error) {
      console.error(error);
      alert('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  // Helper function to check if selected range has unavailable dates
 const hasUnavailableDatesInSelectedRange = useCallback(() => {
    if (!state.searchState.checkin || !state.searchState.checkout || Object.keys(inventoryStatus).length === 0) {
      return false;
    }
    
    const formatDateForCheck = (date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    
    const start = new Date(state.searchState.checkin);
    const end = new Date(state.searchState.checkout);
    let current = new Date(start);
    
    while (current <= end) {
      const dateStr = formatDateForCheck(current);
      const status = inventoryStatus[dateStr];
      if (status && !status.has_available_inventory) {
        return true;
      }
      current.setDate(current.getDate() + 1);
    }
    return false;
  }, [state.searchState.checkin, state.searchState.checkout, inventoryStatus]);

  return (
    <>
     {/* ========================================================== */}
{/* DESKTOP VIEW - FIXED FLEX LAYOUT                          */}
{/* ========================================================== */}
<div className="hidden md:block w-full bg-white border-b border-gray-200 shadow-sm">
  <div className="container mx-auto px-6 lg:px-8 py-4">
    {/* flex-row ensures items sit side-by-side; items-end aligns labels and boxes */}
    <div className="flex flex-row items-end justify-start gap-4">
      
      {/* 1. Hotel Selector - Set a fixed width to prevent shrinking */}
{/* 1. Hotel Selector */}
<div className="w-[280px] relative group">
  <button
    type="button"
    onClick={() => setHotelDropdownOpen(!hotelDropdownOpen)}
    className={`w-full h-12 text-left px-4 border rounded-xl transition-all flex items-center justify-between bg-white shadow-sm ${
      hotelDropdownOpen ? 'border-brand-accent ring-4 ring-brand-accent/5' : 'border-gray-200 hover:border-gray-300'
    }`}
  >
    <div className="flex items-center gap-3 overflow-hidden">
      <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
     <div className="flex flex-col min-w-0"> 
  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">
    Hotel
  </span>
  <span className="truncate font-bold text-gray-900 text-[14px] leading-tight">
    {state.searchState.hotel || 'Select Hotel'}
  </span>
</div>
    </div>
    <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${hotelDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/>
    </svg>
  </button>
        
        {hotelDropdownOpen && (
          <div className="absolute top-full left-0 w-full z-50 bg-white border border-gray-100 rounded-2xl shadow-2xl mt-2 p-2 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative mb-2">
              <input
                type="text"
                value={hotelSearch}
                onChange={(e) => setHotelSearch(e.target.value)}
                placeholder="Where are you going?"
                className="w-full h-10 pl-9 pr-4 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-brand-accent/20"
                autoFocus
              />
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {filteredHotels.map((hotel) => (
                <div 
                  key={hotel.id} 
                  className="p-3 text-sm font-medium text-gray-700 cursor-pointer hover:bg-brand-accent/5 hover:text-brand-accent rounded-lg transition-colors" 
                  onClick={() => handleHotelSelect(hotel)}
                >
                  {hotel.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0">
        <CustomDatePicker onInventoryStatusChange={setInventoryStatus} />
      </div>

      {hasUnavailableDatesInSelectedRange() && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2 hidden">
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Some selected dates have no available rooms</span>
        </div>
      )}
      {/* 3. Rooms & Guests - We give this a flexible width with a minimum */}
      <div className="flex-grow max-w-[340px]">
        <GuestsSelector
          isOpen={guestsDropdownOpen}
          onToggle={() => setGuestsDropdownOpen(!guestsDropdownOpen)}
          onUpdate={handleGuestsUpdate}
          rooms={state.rooms}
        />
      </div>

      {/* 5. Promo Code — Desktop */}
<div className="flex-shrink-0 relative" ref={promoRef}>
  <button
    type="button"
    onClick={() => promoApplied ? null : setIsPromoOpen(!isPromoOpen)}
    className={`h-12 px-4 border rounded-xl text-sm transition-all flex items-center gap-2 bg-white whitespace-nowrap ${
      promoApplied
        ? 'border-green-300 bg-green-50 cursor-default'
        : 'border-dashed border-gray-300 text-gray-500 hover:border-brand-accent hover:text-brand-accent font-medium'
    }`}
  >
    <svg className={`w-4 h-4 shrink-0 ${promoApplied ? 'text-green-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3zM6 6h.008v.008H6V6z"/>
    </svg>

    {promoApplied ? (
      <>
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Promo Applied</span>
          <span className="text-[13px] font-bold text-green-800">{state.searchState.promo_code}</span>
        </div>
        <span
          role="button"
          onClick={handleClearPromo}
          title="Remove promo"
          className="ml-1 w-5 h-5 rounded-full bg-green-200 hover:bg-red-100 flex items-center justify-center transition-colors"
        >
          <svg className="w-3 h-3 text-green-700 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </span>
      </>
    ) : (
      'Have a promo code?'
    )}
  </button>

  {/* Popover */}
  {isPromoOpen && !promoApplied && (
    <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 p-4 animate-in fade-in zoom-in-95 duration-150" style={{minWidth: '260px'}}>
      <p className="text-sm font-bold text-gray-800 mb-1">Enter promo code</p>
      <p className="text-xs text-gray-400 mb-3">Code is validated before you search.</p>
     <div className="flex flex-col gap-2">
  <input
    type="text"
    value={promoInput}
    onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
    onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
    placeholder="e.g. SAVE10"
    autoFocus
    disabled={promoLoading}
    className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm font-medium tracking-wider focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent placeholder:normal-case placeholder:tracking-normal disabled:opacity-50"
  />
  <button
    onClick={handleApplyPromo}
    disabled={!promoInput.trim() || promoLoading}
    className="w-full h-10 bg-brand-accent text-white text-sm font-bold rounded-lg hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
  >
    {promoLoading
      ? <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
      : 'Apply'
    }
  </button>
</div>
      {promoError && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd"/>
          </svg>
          {promoError}
        </p>
      )}
    </div>
  )}
</div>

      {/* 4. Optional Search Button (Hidden by default based on your code) */}
      <button onClick={handleSearch} disabled={searchLoading} className="hidden">
        {searchLoading ? 'Searching...' : 'Search'}
      </button>
      
    </div>
  </div>
</div>

      {/* ========================================================== */}
      {/* MOBILE VIEW (Sticky Bar + Modals)                          */}
      {/* ========================================================== */}
      
    {/* 1. Sticky Top Search Bar */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <button 
          onClick={() => setIsMobileSearchOpen(true)}
          className="w-full bg-white text-left px-4 py-3 flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">
              {state.searchState.hotel || 'Where are you going?'}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {state.searchState.checkin && state.searchState.checkout 
                ? `${new Date(state.searchState.checkin).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(state.searchState.checkout).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'Add dates'
              } • {state.rooms.length} Room, {state.rooms.reduce((acc, r) => acc + r.adults, 0)} Adult, {state.rooms.reduce((acc, r) => acc + r.children, 0)} Child
            </div>
          </div>
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 2. Main Mobile Modal */}
      {isMobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-gray-50 flex flex-col animate-in slide-in-from-bottom duration-200">
          <div className="bg-white px-4 py-4 flex items-center justify-between border-b border-gray-100 shadow-sm shrink-0">
            <button onClick={() => setIsMobileSearchOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h2 className="font-bold text-lg text-gray-800">Plan your trip</h2>
            <div className="w-8"></div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            
            {/* DESTINATION TRIGGER */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Destination</label>
              <button 
                onClick={() => setIsDestinationOpen(true)} 
                className="w-full flex items-center bg-gray-50 border border-gray-200 rounded-xl h-12 px-3 text-left"
              >
                <svg className="w-5 h-5 text-brand-accent mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                <span className={`text-base font-semibold ${state.searchState.hotel ? 'text-gray-900' : 'text-gray-400'}`}>
                  {state.searchState.hotel || 'Where to?'}
                </span>
              </button>
            </div>

            {/* Dates */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Dates</label>
              <CustomDatePicker onInventoryStatusChange={setInventoryStatus} />
            </div>

          {/* Rooms & Guests Trigger */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Rooms & Guests</label>
  <button 
    onClick={() => setIsGuestSheetOpen(true)} 
    className="w-full h-12 flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 text-left hover:border-gray-300 transition-all shadow-sm"
  >
    <div className="flex items-center gap-3">
      <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" />
      </svg>
      <span className="font-bold text-gray-900">
        {state.rooms.length} Room{state.rooms.length > 1 ? 's' : ''}, {state.rooms.reduce((acc, r) => acc + r.adults, 0)} Adults, {state.rooms.reduce((acc, r) => acc + r.children, 0)} Children
      </span>
    </div>
    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
    </svg>
  </button>
</div>
            {/* Promo Code — Mobile */}
<div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Promo Code</label>

  {promoApplied ? (
    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl h-12 px-4">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <span className="font-bold text-green-800 text-sm tracking-wider">{state.searchState.promo_code}</span>
        <span className="text-xs text-green-600 font-medium">Applied</span>
      </div>
      <button
        onClick={handleClearPromo}
        className="text-xs font-bold text-red-500 px-2 py-1 hover:bg-red-50 rounded-lg transition-colors"
      >
        Remove
      </button>
    </div>
  ) : (
    <>
      <div className="flex gap-2">
        <input
          type="text"
          value={promoInput}
          onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
          placeholder="Enter promo code"
          disabled={promoLoading}
          className="flex-1 h-12 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium tracking-wider focus:outline-none focus:ring-2 focus:ring-brand-accent/50 focus:border-brand-accent uppercase placeholder:normal-case placeholder:tracking-normal disabled:opacity-50"
        />
        <button
          onClick={handleApplyPromo}
          disabled={!promoInput.trim() || promoLoading}
          className="h-12 px-5 bg-brand-accent text-white text-sm font-bold rounded-xl hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
        >
          {promoLoading
            ? <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
            : 'Apply'
          }
        </button>
      </div>
      {promoError && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd"/>
          </svg>
          {promoError}
        </p>
      )}
    </>
  )}
</div>

<div className="h-20"></div>
            <div className="h-20"></div>
          </div>

          <div className="bg-white border-t border-gray-200 p-4 shrink-0 pb-safe">
            <button onClick={handleSearch} disabled={searchLoading} className="w-full bg-brand-accent h-14 rounded-xl text-white font-bold text-lg shadow-lg flex items-center justify-center gap-2">
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      )}

      {/* 3. DESTINATION FULL SCREEN SHEET */}
      {isDestinationOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col animate-in slide-in-from-right duration-300">
           <div className="flex items-center gap-2 p-4 border-b border-gray-100 shadow-sm shrink-0">
              <button onClick={() => setIsDestinationOpen(false)} className="p-2 rounded-full hover:bg-gray-100">
                 <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="flex-1 relative">
                 <input 
                    autoFocus
                    type="text"
                    className="w-full h-12 pl-10 pr-4 bg-gray-100 rounded-xl font-semibold text-lg outline-none focus:ring-2 focus:ring-brand-accent/50"
                    placeholder="Search hotel name..."
                    value={hotelSearch}
                    onChange={(e) => setHotelSearch(e.target.value)}
                  />
                 <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </div>
           </div>

           <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                 {filteredHotels.length > 0 ? filteredHotels.map(h => (
                   <div 
                     key={h.id} 
                     onClick={() => handleHotelSelect(h)}
                     className="flex items-center gap-3 p-4 border-b border-gray-50 active:bg-gray-50 transition-colors"
                   >
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                      </div>
                      <div>
                          <div className="font-semibold text-gray-900 text-lg">{h.name}</div>
                          <div className="text-sm text-gray-400">Hotel</div>
                      </div>
                   </div>
                 )) : (
                   <div className="text-center py-10 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      No hotels found
                   </div>
                 )}
              </div>
           </div>
        </div>
      )}

      {/* 4. GUEST BOTTOM SHEET */}
      {isGuestSheetOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setIsGuestSheetOpen(false)}></div>
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] animate-in slide-in-from-bottom duration-300">
             <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Select Guests</h3>
                <button onClick={() => setIsGuestSheetOpen(false)} className="p-2 bg-gray-100 rounded-full">
                   <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
             </div>
             <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <GuestsSelector isOpen={true} onUpdate={handleGuestsUpdate} rooms={state.rooms} isMobile={true} />
                <div className="h-20"></div>
             </div>
             <div className="p-4 bg-white border-t border-gray-200 pb-safe">
                <button onClick={() => setIsGuestSheetOpen(false)} className="w-full h-12 bg-brand-accent text-white font-bold rounded-xl shadow-md">Done</button>
             </div>
          </div>
        </>
      )}
    </>
  );
};

// --- GUESTS COMPONENT ---
const GuestsSelector = ({ isOpen, onToggle, onUpdate, rooms, isMobile }) => {
  const [localRooms, setLocalRooms] = useState([...rooms]);
  const [expandedRoom, setExpandedRoom] = useState(0); // For Desktop: Tracks which room is being edited

  useEffect(() => { setLocalRooms([...rooms]); }, [rooms, isOpen]);

const updateRoom = (index, field, value) => {
  const newRooms = [...localRooms];
  
  // Special logic for updating child count to sync with childAges array
  if (field === 'children') {
    const currentChildren = newRooms[index].children;
    let newAges = newRooms[index].childAges ? [...newRooms[index].childAges] : [];

    if (value > currentChildren) {
      // Increasing: Add default age (0) for new child
      newAges.push(0);
    } else if (value < currentChildren) {
      // Decreasing: Remove last child's age
      newAges.pop();
    }
    
    newRooms[index] = { ...newRooms[index], children: value, childAges: newAges };
  } else {
    // Standard update for adults
    newRooms[index] = { ...newRooms[index], [field]: value };
  }

  setLocalRooms(newRooms);
  if(isMobile) onUpdate(newRooms);
};

const handleAgeChange = (roomIndex, childIndex, age) => {
  const newRooms = [...localRooms];
  const newAges = [...newRooms[roomIndex].childAges];
  newAges[childIndex] = parseInt(age);
  newRooms[roomIndex] = { ...newRooms[roomIndex], childAges: newAges };
  setLocalRooms(newRooms);
  if(isMobile) onUpdate(newRooms);
};

  const addRoom = () => {
  //  if (localRooms.length < 5) {
    // UPDATED: Initialize with childAges array
    const newRooms = [...localRooms, { adults: 2, children: 0, childAges: [] }];
    setLocalRooms(newRooms);
    setExpandedRoom(newRooms.length - 1);
    if(isMobile) onUpdate(newRooms);
  // }
};

  const removeRoom = (index) => {
    if (localRooms.length > 1) {
      const newRooms = localRooms.filter((_, i) => i !== index);
      setLocalRooms(newRooms);
      setExpandedRoom(Math.max(0, index - 1)); // Safely shift focus
      if(isMobile) onUpdate(newRooms);
    }
  };

  const handleApply = () => { onUpdate(localRooms); if(onToggle) onToggle(); };

  const totalAdults = localRooms.reduce((acc, r) => acc + r.adults, 0);
  const totalChildren = localRooms.reduce((acc, r) => acc + r.children, 0);
  const guestsText = `${localRooms.length} Room${localRooms.length !== 1 ? 's' : ''}, ${totalAdults} Adult${totalAdults !== 1 ? 's' : ''}, ${totalChildren} Child${totalChildren !== 1 ? 'ren' : ''}`;

  // === MOBILE RENDER (Stacked, Always Expanded) ===
 if (isMobile) {
    return (
      <div className="space-y-4">
        {localRooms.map((room, index) => (
          <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4 border-b border-gray-50 pb-3">
              <span className="font-bold text-gray-800">Room {index + 1}</span>
              {localRooms.length > 1 && <button onClick={() => removeRoom(index)} className="text-red-500 text-xs font-bold uppercase tracking-wider px-2 py-1 bg-red-50 rounded">Remove</button>}
            </div>
            
            {/* Adults Row */}
            <div className="flex justify-between items-center mb-4">
               <div><div className="font-semibold text-gray-700">Adults</div><div className="text-xs text-gray-400">Ages 13+</div></div>
               <div className="flex items-center gap-3"><CounterBtn onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1} icon="-" /><span className="w-6 text-center font-bold text-lg text-gray-900">{room.adults}</span><CounterBtn onClick={() => updateRoom(index, 'adults', room.adults + 1)} disabled={room.adults >= 3} icon="+" /></div>
            </div>
            
            {/* Children Row - Updated Max Limit to 2 */}
            <div className="flex justify-between items-center">
               <div><div className="font-semibold text-gray-700">Children</div><div className="text-xs text-gray-400">Ages 0-12</div></div>
               <div className="flex items-center gap-3"><CounterBtn onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0} icon="-" /><span className="w-6 text-center font-bold text-lg text-gray-900">{room.children}</span><CounterBtn onClick={() => updateRoom(index, 'children', room.children + 1)} disabled={room.children >= 3} icon="+" /></div>
            </div>

            {/* Child Age Dropdowns */}
            {room.children > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-gray-50">
                {Array.from({ length: room.children }).map((_, childIdx) => (
                  <div key={childIdx}>
                    <label className="block text-xs text-gray-500 mb-1">Child {childIdx + 1} Age</label>
                    <select
                      value={room.childAges?.[childIdx] || 0}
                      onChange={(e) => handleAgeChange(index, childIdx, e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-md text-sm bg-white focus:border-brand-accent outline-none"
                    >
                      {[...Array(13).keys()].map(age => (
                        <option key={age} value={age}>{age} years</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <button onClick={addRoom} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold text-sm hover:border-brand-accent hover:text-brand-accent flex items-center justify-center gap-2"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>Add Another Room</button>
      </div>
    );
  }

  // === DESKTOP RENDER (Accordion / Summary Style) ===
  return (
   <div className="relative min-w-[280px]">
    <button 
      type="button" 
      id="guests-button" 
      onClick={onToggle} 
      className={`w-full h-12 text-left px-4 border rounded-xl transition-all flex items-center justify-between bg-white shadow-sm ${
        isOpen ? 'border-brand-accent ring-4 ring-brand-accent/5' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-3 overflow-hidden">
        <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <div className="flex flex-col">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">
            Rooms & Guests
          </span>
          <span className="truncate font-bold text-gray-900 text-[14px] leading-tight">
            {localRooms.length} Room{localRooms.length !== 1 ? 's' : ''}, {totalAdults} Adults, {totalChildren} Children
          </span>
        </div>
      </div>
      <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

      {isOpen && (
        <div className="absolute top-full right-0 w-80 mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-50 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-100">
           <div className="max-h-[300px] overflow-y-auto">
             {localRooms.map((room, index) => {
               const isExpanded = expandedRoom === index;
               
    if (isExpanded) {
                  // --- RENDER ACTIVE EDITING ROOM (DESKTOP) ---
                  return (
                    <div key={index} className="p-4 border-b border-gray-100 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-bold text-dark">Room {index + 1}</span>
                        {localRooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-xs text-red-500 hover:bg-red-50 px-2 py-1 rounded">Remove</button>
                        )}
                      </div>
                      
                      {/* Adults Row */}
                      <div className="flex justify-between items-center mb-3">
                          <div className="text-sm text-gray-700">Adults <span className="text-xs text-gray-400 block">(12+ yrs)</span></div>
                          <div className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50 disabled:opacity-50" onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1}>-</button>
                            <span className="w-5 text-center font-bold text-gray-800">{room.adults}</span>
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50" onClick={() => updateRoom(index, 'adults', room.adults + 1)} disabled={room.adults >= 3}>+</button>
                          </div>
                      </div>

                      {/* Children Row - LIMIT UPDATED TO 2 */}
                      <div className="flex justify-between items-center">
                          <div className="text-sm text-gray-700">Children <span className="text-xs text-gray-400 block">(0-11 yrs)</span></div>
                          <div className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50 disabled:opacity-50" onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0}>-</button>
                            <span className="w-5 text-center font-bold text-gray-800">{room.children}</span>
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50" onClick={() => updateRoom(index, 'children', room.children + 1)} disabled={room.children >= 2}>+</button>
                          </div>
                      </div>

                      {/* Child Age Dropdowns (ADDED) */}
                      {room.children > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-2 pt-2 border-t border-gray-50">
                          {Array.from({ length: room.children }).map((_, childIdx) => (
                            <div key={childIdx}>
                              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Child {childIdx + 1} Age</label>
                              <select
                                value={room.childAges?.[childIdx] || 0}
                                onChange={(e) => handleAgeChange(index, childIdx, e.target.value)}
                                className="w-full p-1.5 border border-gray-200 rounded text-sm bg-white focus:border-brand-accent outline-none cursor-pointer"
                              >
                                {[...Array(13).keys()].map(age => (
                                  <option key={age} value={age}>{age} years</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                } else {
                 return (
                   <div key={index} className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors">
                     <div>
                       <div className="font-bold text-xs text-gray-500 uppercase tracking-wide mb-0.5">Room {index + 1}</div>
                       <div className="text-sm font-semibold text-gray-800">
                         {room.adults} Adults, {room.children} Children
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedRoom(index)} className="text-brand-accent text-sm font-bold px-2 py-1 hover:bg-brand-accent/5 rounded">Edit</button>
                        {localRooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-gray-400 hover:text-red-500 p-1">
                             <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                     </div>
                   </div>
                 );
               }
             })}
           </div>
           
           <div className="p-3 bg-white border-t border-gray-100 flex justify-between items-center gap-3">
              
                <button onClick={addRoom} className="text-brand-accent text-sm font-bold hover:underline py-2">+ Add Room</button>
               <div />
              <button onClick={handleApply} className="bg-brand-accent-hover text-white px-6 py-2 rounded-md font-bold text-sm shadow hover:bg-brand-accent-hover transition-colors">Apply</button>
           </div>
        </div>
      )}
    </div>
  );
};

const CounterBtn = ({ onClick, disabled, icon }) => (
  <button onClick={onClick} disabled={disabled} className="w-10 h-10 rounded-full border border-gray-200 bg-white text-brand-accent flex items-center justify-center font-bold text-xl shadow-sm disabled:opacity-30 disabled:bg-gray-50 active:scale-90 transition-transform">{icon}</button>
);

export default SearchBar;